import gfppDataUtils from '../../utils/gfppDataUtils';
import experiment from 'experiment';
import constants from '#packages/constants';
import * as stateManagement from '#packages/stateManagement';
import type { EditorAPI } from '#packages/editorAPI';
import type { CompRef, Link } from '@wix/document-services-types';
import { ErrorReporter } from '@wix/editor-error-reporter';
import * as util from '#packages/util';
import { fedopsLogger } from '#packages/util';

const getMetaSiteInstance = (editorAPI: EditorAPI) =>
  editorAPI.dsRead.platform.getAppDataByApplicationId(
    constants.APPLICATIONS.META_SITE_APPLICATION_ID,
  )?.instance;

const { ACTIONS } = constants.ROOT_COMPS.GFPP;
const editorProviderAppId = '8eb6f7f2-f101-47aa-b307-016f8661b912';

async function openWixelEditor(
  editorAPI: EditorAPI,
  compRef: CompRef,
  isEditWithWixelPermitted: boolean,
) {
  fedopsLogger.interactionStarted(fedopsLogger.INTERACTIONS.WIXEL.OPEN_EDITOR);

  try {
    const uri = editorAPI.components.data.get(compRef).uri;
    const fileId = (uri.includes('/') ? uri.split('/').pop() : uri) || uri;
    const pageId = editorAPI.components.getPage(compRef)?.id;
    const externalId = (compRef as AnyFixMe)[0].id;

    const params = new URLSearchParams({
      instance: getMetaSiteInstance(editorAPI),
      fileId,
      externalId,
      appId: editorProviderAppId,
      externalConfig: JSON.stringify({ pageId }),
      referrer: window.name,
    });

    editorAPI.panelManager.openPanel('panels.focusPanels.wixelEditorPanel', {
      // eslint-disable-next-line no-restricted-syntax
      url: `${util.serviceTopology.businessManagerDomain}/_api/www-editor-serverless/wixel-redirect?${params}`,
    });

    const isFireAndForgetSave = experiment.isOpen(
      'specs.app-market.WixelFireAndForgetSave',
    );
    if (!isFireAndForgetSave) {
      await new Promise((resolve, reject) => {
        editorAPI.saveManager.saveInBackground(
          resolve,
          reject,
          'edit-with-wixel',
        );
      });
    }

    fedopsLogger.interactionEnded(fedopsLogger.INTERACTIONS.WIXEL.OPEN_EDITOR, {
      customParams: { step: 'panel_opened' },
    });
  } catch (error) {
    fedopsLogger.interactionEnded(fedopsLogger.INTERACTIONS.WIXEL.OPEN_EDITOR, {
      customParams: {
        step: 'error',
        errorMessage: error instanceof Error ? error.message : String(error),
      },
    });
    ErrorReporter.captureException(error, {
      tags: {
        wixelEditImageFailure: true,
        isEditWithWixelPermitted,
      },
    });
  }
}

const aiToolsPanel = 'compPanels.dynamicPanels.aiToolsPanel';

const isAiToolsPanelForImageEnabled = experiment.isOpen(
  'se_aiToolsPanelForImage',
);

function isAIToolsPanelOpen(editorAPI: EditorAPI) {
  return editorAPI.panelManager.isPanelOpened(aiToolsPanel);
}

function getMainActions(editorAPI: EditorAPI, compRef: CompRef) {
  const isAiImageExperimentOpen = experiment.isOpen('specs.mdst.AiTextToImage');
  const isEditWithWixelEnabled = experiment.isOpen(
    'wwwEditWithWixelInClassicEditor',
  );
  const isWixelRoleSyncEnabled = experiment.isOpen(
    'specs.www.wixel-role-sync-enabled',
  );
  const isEditWithWixelPermitted = isWixelRoleSyncEnabled
    ? editorAPI.platform.permissions.isCustomPermissionsGranted([
        'WIXEL.SITE_EDIT_WITH_WIXEL',
      ])
    : editorAPI.account.isAccountOwner();

  const changeImage = {
    label: 'gfpp_mainaction_image',
    actionType: ACTIONS.CHANGE_MEDIA,
    isApplied: gfppDataUtils.getDataDefinedFn('uri'),
    onClick(editorAPI: EditorAPI, compRef: CompRef, origin: string) {
      editorAPI.mediaServices.changeWPhoto(compRef, origin);
    },
  };

  function getEditImage(): AnyFixMe {
    const isDescendantOfRepeaterItem =
      editorAPI.components.is.descendantOfRepeaterItem(compRef);

    const isAutosaveOn = !!editorAPI.autosaveManager.isAutosaveEnabled();
    const isDraft = editorAPI.dsRead.generalInfo.isDraft();

    const isEligibleForWixel =
      isEditWithWixelPermitted &&
      !isDescendantOfRepeaterItem &&
      isAutosaveOn &&
      !isDraft;

    if (!isEditWithWixelEnabled || !isEligibleForWixel) {
      return {
        label: 'gfpp_main_action_edit_image',
        automationId: 'gfpp-edit-image-aid',
        onClick(editorAPI: EditorAPI, compRef: CompRef, origin: string) {
          if (isEligibleForWixel) {
            fedopsLogger.interactionStarted(
              fedopsLogger.INTERACTIONS.WIXEL.ELIGIBLE_FOR_WIXEL,
            );
          }
          fedopsLogger.interactionStarted(
            fedopsLogger.INTERACTIONS.WIXEL.OPEN_PHOTO_STUDIO,
          );
          editorAPI.mediaServices.adjustWPhoto(compRef, origin);
        },
      };
    }

    return {
      label: 'gfpp_main_action_edit_image',
      automationId: 'gfpp-edit-with-wixel-image-aid',
      onClick(editorAPI: EditorAPI, compRef: CompRef) {
        fedopsLogger.interactionStarted(
          fedopsLogger.INTERACTIONS.WIXEL.ELIGIBLE_FOR_WIXEL,
        );
        openWixelEditor(editorAPI, compRef, isEditWithWixelPermitted);
      },
    };
  }

  const editImage = getEditImage();

  const aiImageGeneratorIcon: AnyFixMe = {
    label: undefined,
    icon: 'sparklesBold',
    tooltip: 'add_section_label_ai_image',
    className: 'gfpp-btn_ai-icon',
    automationId: 'gfpp-ai-image-creator-aid',
    onClick: (editorAPI: EditorAPI, compRef: CompRef) => {
      editorAPI.mediaServices.generateWPhoto(compRef, 'editor-image-gfpp');
    },
  };

  const aiImageToolsIcon: AnyFixMe = {
    label: undefined,
    icon: !isAIToolsPanelOpen(editorAPI)
      ? 'sparklesBoldAnimation'
      : 'sparklesBold',
    tooltip: 'ai_tools_gfpp_main_action',
    className: 'gfpp-btn_ai-icon_animation',
    automationId: 'gfpp-ai-image-tools-aid',
    onClick: gfppDataUtils.getTogglePanelFn('aiTools'),
    isSelected: gfppDataUtils.getPanelStateFn('aiTools'),
    customBI: {
      action_name: 'ai_tools_menu',
      icon: 'ai_tools_menu',
    },
  };

  const mainActions = [changeImage];
  const shouldEnableAIFeaturesInSchoolMode =
    stateManagement.schoolMode.selectors.shouldEnableAIFeatures(
      editorAPI.store.getState(),
    );
  if (shouldEnableAIFeaturesInSchoolMode) {
    if (isAiToolsPanelForImageEnabled) {
      mainActions.unshift(aiImageToolsIcon);
    } else if (isAiImageExperimentOpen) {
      mainActions.unshift(aiImageGeneratorIcon);
    }
  }

  mainActions.push(editImage);

  return mainActions;
}

const wphotoGfppData = {
  translateAction: ACTIONS.SETTINGS,
  mainActions: getMainActions,
  enabledActions: [
    ACTIONS.SETTINGS,
    ACTIONS.DESIGN,
    ACTIONS.CROP,
    ACTIONS.FILTERS,
    ACTIONS.ANIMATION,
    ACTIONS.LINK,
    ACTIONS.HELP,
  ],
  mobileEnabledActions(editorAPI: EditorAPI, compRef: CompRef) {
    const actions: Array<string> = [
      ACTIONS.SETTINGS,
      ACTIONS.HIDE,
      ACTIONS.HELP,
      ACTIONS.ANIMATION,
    ];
    if (!editorAPI.components.is.descendantOfRepeaterItem(compRef)) {
      actions.push(ACTIONS.CROP);
    }
    return actions;
  },
  presetActions: {
    help: {
      helpId: '41a182e0-3b77-4731-af2f-38c6dbf6e982',
      mobileHelpId: 'da88cf75-37c5-4bcc-b1b6-9c0df84c29e0',
    },
    link: {
      onClick(editorAPI: EditorAPI, compRef: CompRef) {
        editorAPI.closeCompPanelIfExists();
        editorAPI.openLinkPanel({
          link: editorAPI.components.data.get(compRef).link,
          callback(newLinkData: Link) {
            const { onClickBehavior } =
              editorAPI.components.properties.get(compRef);
            const isSwitchedToUrl =
              newLinkData && onClickBehavior === 'disabled';
            const isSwitchedToNonUrl =
              onClickBehavior === 'goToLink' && !newLinkData;

            if (isSwitchedToUrl || isSwitchedToNonUrl) {
              editorAPI.components.properties.update(compRef, {
                onClickBehavior: isSwitchedToUrl ? 'goToLink' : 'disabled',
              });
            }

            editorAPI.components.data.update(compRef, { link: newLinkData });
          },
        });
      },
    },
  },
};

export default wphotoGfppData;
